import {inject, Injectable} from '@angular/core';
import {AppStore} from '../stores/app.store';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ListViews} from '../models/list/list-views.model';
import {HistoryStoreItem} from '../models/history/history-store-item.model';
import {Preferences} from '../api/models/preferences';
import {isObject} from '../util/is-object.util';
import {LoginType} from '../types/login/login.type';
import {QueryService} from '../modules/akita-mimic/services/query.service';
import {AppState} from '../modules/akita-mimic/models/store-states/app-state.model';
import {ListItemTypeEnum} from '../enums/list-item/list-item-type.enum';

@Injectable({ providedIn: 'root' })
export class AppQuery extends QueryService<AppState> {
    public isDisconnected$: Observable<boolean> = this.select('disconnected');
    public isSearchTaskGlobalScope$: Observable<boolean> = this.select('isSearchAndTaskGlobalScope');
    public showSearchOverlay$: Observable<boolean> = this.select('showSearchMenu');
    public isShowingHistoryOverlay$: Observable<boolean> = this.select('isShowingHistoryOverlay');
    public history$: Observable<Array<HistoryStoreItem>> = this.select('navigationHistory');
    public listViews$: Observable<ListViews> = this.select('listViews');
    public isShowingShortMenuSidebar$: Observable<boolean> = this.select('isShowingShortMenuSidebar');
    public isShowingShortMenuContent$: Observable<boolean> = this.select('isShowingShortMenuContent');
    public isShowingDebugMenu$: Observable<boolean> = this.select('showDebugMenu');
    public preferences$: Observable<Preferences> = this.select('preferences');
    public hasActionButton$: Observable<boolean> = this.select('hasActionButton');
    public isInitialized$: Observable<boolean> = this.select('isInitialized');
    public deleteVaultWithoutPrompt$: Observable<boolean> = this.select('deleteVaultWithoutPrompt');

    public constructor() {
        const store = inject(AppStore);
        super(store);
    }

    public getListViewByListName(listName: string): ListItemTypeEnum | undefined {
        const listViews = this.getValue().listViews;
        if (listName in listViews) {
            const listItemType = listViews[listName];
            if (isObject(listItemType)) {
                const listItemTypeOld = listItemType as unknown as { listDisplayType: number };
                return Object.values(ListItemTypeEnum)[listItemTypeOld.listDisplayType];
            }
            return listItemType;
        }
        return undefined;
    }

    public getListViewObservableByListName(listName: string): Observable<ListItemTypeEnum | undefined> {
        return this.listViews$.pipe(map(listViews => {
            const listItemType = listViews[listName];
            if (isObject(listItemType)) {
                const listItemTypeOld = listItemType as unknown as { listDisplayType: number };
                return Object.values(ListItemTypeEnum)[listItemTypeOld.listDisplayType];
            }
            return listViews[listName];
        }));
    }

    public getIsSearchAndTaskGlobalScope(): boolean {
        return this.getValue().isSearchAndTaskGlobalScope;
    }

    public getIsNotShowingSearchOnGoingBack(): boolean {
        return this.getValue().isNotShowingSearchOnGoingBack;
    }

    public getIsShowingTaskSearch(): boolean {
        return this.getValue().showSearchMenu;
    }

    public getLastNavigationHistoryItem(): HistoryStoreItem | undefined {
        return this.getValue()
            .navigationHistory
            .at(0);
    }

    public getNavigationHistory(): Array<HistoryStoreItem> {
        return this.getValue().navigationHistory;
    }

    public getCurrentVersion(): string {
        return this.getValue().currentVersion;
    }

    public getToastDelayMS(): number {
        return this.getValue().toastDelayMS;
    }

    public getMaxImagesPerWorker(): number {
        return this.getValue().maxImagesPerWorker;
    }

    public getIsInitialized(): boolean {
        return this.getValue().isInitialized;
    }

    public getIsDisconnected(): boolean {
        return this.getValue().disconnected;
    }

    public getConnectionErrorTimeStamp(): number {
        return this.getValue().connectionErrorTimeStamp;
    }

    public getLastLoginType(): LoginType {
        return this.getValue().lastLoginType;
    }

    public getDeleteVaultWithoutPrompt(): boolean {
        return this.getValue().deleteVaultWithoutPrompt;
    }
}
